// =============================================================================
// Shared Kapio date helpers — single source of truth for all four apps.
//
// Supabase TIMESTAMP WITHOUT TIME ZONE returns UTC without a Z suffix, so
// `new Date(str)` interprets it as local time and every display shifts by
// the user's offset (CAT = +2 → "5m ago" became "2h ago"). parseTimestamp
// appends Z when the string has no offset so JS treats it correctly as UTC.
// =============================================================================

/**
 * Parse a Supabase timestamp string, ensuring UTC interpretation when no
 * timezone is present. Returns a Date or null.
 */
export function parseTimestamp(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  const s = String(ts).trim();
  // Already has timezone: Z, +HH:MM, -HH:MM, +HHMM, -HHMM
  if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  // Naive timestamp — append Z so it's interpreted as UTC
  return new Date(s.replace(" ", "T") + "Z");
}

/**
 * Relative time: "just now" / "5m ago" / "3h ago" / "2d ago" / "1w ago" /
 * "Mar 5". Falls back to short month-day for older dates.
 */
export function relativeTime(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "";
  const diff = Date.now() - d.getTime();
  if (diff < 0) return "just now"; // clock skew guard
  const m = Math.floor(diff / 60_000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  const w = Math.floor(days / 7);
  if (w < 5)   return `${w}w ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "30 Mar 2026" — for review dates, payout history. */
export function formatDateShort(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", {
    day:   "numeric",
    month: "short",
    year:  "numeric",
  });
}

/** "30 Mar, 05:15" — for order timelines, message bubble timestamps. */
export function formatDateTime(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "—";
  return d.toLocaleString("en-GB", {
    day:    "numeric",
    month:  "short",
    hour:   "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** "5 Mar 2026, 15:30" — full timestamp for admin tables, audit logs. */
export function formatDateTimeFull(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-ZM", {
    day:    "numeric",
    month:  "short",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  });
}

/** "05:15" — time only, 24-hour, for chat bubbles. */
export function formatTime(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "";
  return d.toLocaleTimeString("en-GB", {
    hour:   "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** "Mar 2026" — for join dates. */
export function formatMonthYear(ts) {
  const d = parseTimestamp(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

/** Whether a given date/timestamp is in the past. */
export function isPast(ts) {
  const d = parseTimestamp(ts);
  return d ? d < new Date() : false;
}
